import React, { useCallback, useContext, useMemo } from 'react';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import { dataRoomMembersNotAdmins } from 'router/subrouters/DataRoom/utils';
import {
  getHasElementsInArray,
  sortByName,
  isSingleElementArray,
  sortUsersByNameThenEmail,
  toggleValueInArray,
} from 'utils';
import MembersGroupsPicker from './components/MembersGroupsPicker';
import SelectedMembers from './components/SelectedMembers';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';

type DataRoomMembersAccessFieldProps = {
  name: string;
  hideSelected?: boolean;
};

const DataRoomMembersAccessField: React.FC<DataRoomMembersAccessFieldProps> = (
  props,
) => {
  const { name, hideSelected } = props;

  const { t } = useTranslation();

  const { allMembers, sellSideMembers, buySideGroups } =
    useContext(MembersContext);

  const {
    input: { value = [], onChange },
  } = useField(name);

  const buySideGroupsWithoutAdmins = useMemo(
    () =>
      buySideGroups
        .map(({ _id, name: groupName, dataRoomMembers }) => ({
          id: _id,
          name: groupName,
          members: sortUsersByNameThenEmail(
            dataRoomMembersNotAdmins(dataRoomMembers),
          ),
        }))
        .filter(({ members }) => getHasElementsInArray(members)),
    [buySideGroups],
  );

  const individualBuyerGroups = useMemo(
    () =>
      buySideGroupsWithoutAdmins
        .filter(({ members }) => isSingleElementArray(members))
        .map((group) => ({ ...group, name: t('individualBuyer') })),
    [buySideGroupsWithoutAdmins, t],
  );

  const individualBuyers = useMemo(
    () =>
      sortUsersByNameThenEmail(
        individualBuyerGroups.flatMap(({ members }) => members),
      ),
    [individualBuyerGroups],
  );

  const multiMemberGroups = useMemo(
    () =>
      sortByName(
        buySideGroupsWithoutAdmins.filter(
          ({ members }) => !isSingleElementArray(members),
        ),
      ),
    [buySideGroupsWithoutAdmins],
  );

  const sellSideGroup = useMemo(() => {
    const sellSideWithoutAdmins = sortUsersByNameThenEmail(
      dataRoomMembersNotAdmins(sellSideMembers),
    );

    return getHasElementsInArray(sellSideWithoutAdmins)
      ? {
          id: 'sell-side',
          name: t('sellSide'),
          members: sellSideWithoutAdmins,
        }
      : null;
  }, [sellSideMembers, t]);

  const allGroups = useMemo(
    () => [
      ...individualBuyerGroups,
      ...multiMemberGroups,
      ...(sellSideGroup ? [sellSideGroup] : []),
    ],
    [individualBuyerGroups, multiMemberGroups, sellSideGroup],
  );

  const selectedMembers = useMemo(
    () =>
      value
        .map((id: string) => allMembers.find((member) => member._id === id))
        .filter(Boolean),
    [allMembers, value],
  );

  const toggleMember = useCallback(
    (memberId: string) => onChange(toggleValueInArray(value, memberId)),
    [value, onChange],
  );

  const toggleGroup = useCallback(
    (members: DataRoomMember[]) => {
      const memberIds = members.map(({ _id }) => _id);
      const allSelected = memberIds.every((id) => value.includes(id));

      onChange(
        allSelected
          ? value.filter((id: string) => !memberIds.includes(id))
          : [...value, ...memberIds.filter((id) => !value.includes(id))],
      );
    },
    [onChange, value],
  );

  const clearAll = useCallback(() => onChange([]), [onChange]);

  return (
    <>
      <MembersGroupsPicker
        individualBuyers={individualBuyers}
        multiMemberGroups={multiMemberGroups}
        sellSideGroup={sellSideGroup}
        selectedIds={value}
        onToggleMember={toggleMember}
        onToggleGroup={toggleGroup}
      />
      {!hideSelected && (
        <SelectedMembers
          selectedMembers={selectedMembers}
          groups={allGroups}
          onRemove={toggleMember}
          onClearAll={clearAll}
        />
      )}
    </>
  );
};

export default DataRoomMembersAccessField;
